import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { 
  FolderKanban, FileText, MessageSquare, HelpCircle, 
  Image, TrendingUp, Building2, Users, Wrench, Award, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      label: 'Projetos', 
      value: stats?.total_projects || 0, 
      icon: FolderKanban, 
      color: 'bg-blue-500',
      description: 'Condomínios atendidos',
      link: '/admin/projects'
    },
    { 
      label: 'Serviços', 
      value: stats?.total_services || 0, 
      icon: Wrench, 
      color: 'bg-cyan-500',
      description: 'Serviços oferecidos',
      link: '/admin/services'
    },
    { 
      label: 'Diferenciais', 
      value: stats?.total_differentials || 0, 
      icon: Award, 
      color: 'bg-amber-500',
      description: 'Diferenciais da empresa',
      link: '/admin/differentials'
    },
    { 
      label: 'Posts do Blog', 
      value: stats?.total_blog_posts || 0, 
      icon: FileText, 
      color: 'bg-green-500',
      description: 'Artigos publicados',
      link: '/admin/blog'
    },
    { 
      label: 'Depoimentos', 
      value: stats?.total_testimonials || 0, 
      icon: MessageSquare, 
      color: 'bg-orange-500',
      description: 'Avaliações de clientes',
      link: '/admin/testimonials'
    },
    { 
      label: 'FAQs', 
      value: stats?.total_faqs || 0, 
      icon: HelpCircle, 
      color: 'bg-purple-500',
      description: 'Perguntas frequentes',
      link: '/admin/faqs'
    },
    { 
      label: 'Galeria', 
      value: stats?.total_gallery_items || 0, 
      icon: Image, 
      color: 'bg-pink-500',
      description: 'Fotos de trabalhos',
      link: '/admin/gallery'
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Visão geral do conteúdo do site</p>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#15448A]"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {statCards.map((stat, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{stat.label}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
                      <p className="text-xs text-gray-400 mt-1">{stat.description}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                      <stat.icon size={24} className="text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp size={20} />
              <span>Ações Rápidas</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <a href="/admin/projects" className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <FolderKanban className="text-blue-600 mb-2" size={24} />
                <p className="font-medium text-gray-900">Adicionar Projeto</p>
                <p className="text-sm text-gray-500">Novo condomínio atendido</p>
              </a>
              <a href="/admin/blog" className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <FileText className="text-green-600 mb-2" size={24} />
                <p className="font-medium text-gray-900">Criar Artigo</p>
                <p className="text-sm text-gray-500">Novo post no blog</p>
              </a>
              <a href="/admin/testimonials" className="p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                <MessageSquare className="text-orange-600 mb-2" size={24} />
                <p className="font-medium text-gray-900">Novo Depoimento</p>
                <p className="text-sm text-gray-500">Adicionar avaliação</p>
              </a>
              <a href="/admin/gallery" className="p-4 bg-pink-50 rounded-lg hover:bg-pink-100 transition-colors">
                <Image className="text-pink-600 mb-2" size={24} />
                <p className="font-medium text-gray-900">Upload de Foto</p>
                <p className="text-sm text-gray-500">Adicionar à galeria</p>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-[#15448A] to-[#0d2f5e] text-white">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center">
                <Building2 size={32} />
              </div>
              <div>
                <h3 className="text-xl font-bold">LUVCORP Soluções Prediais</h3>
                <p className="text-blue-100">Painel de gestão de conteúdo do site</p>
                <p className="text-sm text-blue-200 mt-2">
                  Gerencie projetos, blog, depoimentos e mais.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
